import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useOktaAuth } from '@okta/okta-react';
import IFeedback from '../../models/iFeedback';
import { submitFeedback } from '../../providers/feedbackApi';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import Popover from '@mui/material/Popover';
import { useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import { ResultInfo, ProductInfo, MaterialInfo } from '../../types/ResultTypes';

export interface IResultsFeedbackProps {
  logId: string;
  userQuery?: string;
  llmResponse?: string;
  topFiveResults?: ResultInfo[];
}

type FeedbackType = 'THUMBS_UP' | 'THUMBS_DOWN';

const ResultsFeedback = ({ logId, userQuery, llmResponse, topFiveResults }: IResultsFeedbackProps) => {
  const { authState } = useOktaAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [comment, setComment] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType>();

  /**
   * Sets the anchor element to the event target (thmb down icon) to open the comment box
   * @param event
   */
  const handleNegativeFeedbackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Clears the comment and anchor element
   */
  const handleCommentBoxClose = () => {
    setAnchorEl(null);
    setComment('');
  };

  const formatComment = (comment: string) => {
    const topFiveResultsString =
      topFiveResults
        ?.map((result) => {
          if ((result as ProductInfo).productId !== undefined) {
            result = result as ProductInfo;
            return `Product Id: ${result.productId}, Category: ${result.category}, URL: ${result.link}\n`;
          } else if ((result as MaterialInfo).materialId !== undefined) {
            result = result as MaterialInfo;
            return `Material Id: ${result.materialId}, Material: ${result.material},  URL: ${result.link}\n`;
          } else {
            return '';
          }
        })
        .join('') || '';
    const formattedComment = comment == '' ? 'No comment provided' : comment;
    return (
      'User Comment: \n' +
      formattedComment.trim() +
      '\n\nLog Id: \n' +
      logId.trim() +
      '\n\nUser Query: \n' +
      userQuery?.trim() +
      '\n\nLLM Summary: \n' +
      llmResponse?.trim() +
      '\n\nResults(top 5): \n' +
      topFiveResultsString
    );
  };
  /**
   * Submit feedback and close the comment box
   * @param type
   */
  const handleFeedback = async (rating: FeedbackType) => {
    if (authState?.accessToken?.accessToken) {
      const feedbackResponse: IFeedback = {
        rating,
        comment: rating === 'THUMBS_DOWN' ? formatComment(comment) : undefined,
      };
      handleCommentBoxClose();
      setSelectedFeedback(rating);
      try {
        await submitFeedback(authState?.accessToken?.accessToken, feedbackResponse, logId);
      } catch (error) {
        setSelectedFeedback(undefined);
        toast.error('Failed to submit feedback');
      }
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
      <IconButton
        onClick={() => handleFeedback('THUMBS_UP')}
        title={'Good response'}
        sx={{ color: selectedFeedback === 'THUMBS_UP' ? '#03387E' : '' }}
      >
        <ThumbUpIcon />
      </IconButton>
      <IconButton
        onClick={handleNegativeFeedbackClick}
        title={'Bad response'}
        sx={{ color: selectedFeedback === 'THUMBS_DOWN' ? '#03387E' : '' }}
      >
        <ThumbDownIcon />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCommentBoxClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div style={{ padding: '10px' }}>
          <TextField
            label="Comment"
            multiline
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={() => handleFeedback('THUMBS_DOWN')} sx={{ mt: 1 }}>
            Submit
          </Button>
        </div>
      </Popover>
    </Box>
  );
};

export default ResultsFeedback;
